import React, { useEffect, useState } from 'react';
import { Sparkles, ShieldCheck, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import api from '../lib/api';

interface DisclosurePayload {
  providers: string[];
  data_sent: string[];
  purpose: string;
  retention: string;
  revoke: string;
}

interface ConsentResponse {
  consented: boolean;
  consent_at?: string;
  consent_version?: string;
  current_version: string;
  disclosure: DisclosurePayload;
}

interface AIConsentModalProps {
  /** Controlled visibility (e.g. when user clicks an AI feature and got a 403). */
  open: boolean;
  onClose: () => void;
  /** Fires after the user accepts so the parent can retry the original action. */
  onAccepted?: () => void;
}

/**
 * Apple App Review 5.1.1 / 5.1.2 disclosure + consent modal.
 *
 * Shown before *any* user data leaves the device for an AI provider. Lists
 * exactly what is sent, who receives it, why, retention, and how to revoke.
 * Backend gates all /api/ai/* endpoints — they return 403 + `AI_CONSENT_REQUIRED`
 * until this modal is accepted.
 */
export default function AIConsentModal({ open, onClose, onAccepted }: AIConsentModalProps) {
  const [data, setData] = useState<ConsentResponse | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/ai/consent').then((r) => setData(r.data)).catch(() => setData(null));
  }, [open]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post('/ai/consent');
      onAccepted?.();
      onClose();
    } finally {
      setAccepting(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="ai-consent-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl max-h-[90dvh] overflow-hidden flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-dutch-teal to-cyan-500 flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-neutral-900">AI features</h2>
              <p className="text-xs text-neutral-500">Please review before continuing</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="ai-consent-close"
            className="p-1.5 rounded-full hover:bg-neutral-100 text-neutral-400"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 text-sm text-neutral-700 leading-relaxed space-y-4">
          <p>
            DutchDate uses artificial intelligence to power features like bio
            writing, conversation coaching, and date suggestions. To do that,
            some of your data is sent to a third-party AI provider.
          </p>

          {data ? (
            <>
              <Section icon={<ShieldCheck size={16} className="text-dutch-teal" />} title="Who receives your data">
                <p>{data.disclosure.providers.join(', ')}. We route requests through these providers' commercial APIs.</p>
              </Section>

              <Section icon={<Sparkles size={16} className="text-dutch-teal" />} title="What is sent">
                <ul className="list-disc pl-5 space-y-1">
                  {data.disclosure.data_sent.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              </Section>

              <Section icon={<Sparkles size={16} className="text-dutch-teal" />} title="Why">
                <p>{data.disclosure.purpose}</p>
              </Section>

              <Section icon={<ShieldCheck size={16} className="text-dutch-teal" />} title="Retention">
                <p>{data.disclosure.retention}</p>
              </Section>

              <Section icon={<ShieldCheck size={16} className="text-dutch-teal" />} title="Your control">
                <p>{data.disclosure.revoke}</p>
              </Section>
            </>
          ) : (
            <div className="flex items-center justify-center py-8 text-neutral-400">
              <Loader2 className="animate-spin" size={20} />
            </div>
          )}

          <p className="text-xs text-neutral-500">
            Full details in our{' '}
            <Link to="/privacy" className="text-dutch-teal underline">
              Privacy Policy
            </Link>{' '}
            (section "Artificial Intelligence").
          </p>
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-100 px-6 py-4 flex flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="ai-consent-decline"
            className="flex-1"
          >
            Not now
          </Button>
          <Button
            onClick={accept}
            disabled={accepting || !data}
            data-testid="ai-consent-accept"
            className="flex-1 bg-dutch-teal hover:bg-dutch-teal-dark text-white"
          >
            {accepting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            I understand &amp; agree
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="font-semibold text-neutral-900 text-[13px]">{title}</span>
      </div>
      <div className="text-[13px] text-neutral-600 pl-6">{children}</div>
    </div>
  );
}
